import { MixpanelEvent, MixpanelEventProperty, type MixpanelEventSource, useMixpanel } from '@talos/kyoko';
import { useCallback, useEffect, useMemo } from 'react';
import { getIsBBOEnabled, setIsBBOEnabled } from '../bbo';
import { getShowLegMarketSelector, setShowLegMarketSelector } from '../legMarketSelectorVisibility';
import { getAlignPricesOnDecimal, setAlignPricesOnDecimal } from '../priceAlignment';
import type { PriceGridSettingsContextValue } from '../PriceGridSettingsContext';
import { getShowRFQ, setShowRFQ } from '../rfqVisibility';
import type { PriceGridFilterState } from '../types';

export function usePriceGridSettings(
  filterState: PriceGridFilterState,
  setFilterState: (state: PriceGridFilterState) => void,
  source: MixpanelEventSource
): PriceGridSettingsContextValue {
  const mixpanel = useMixpanel();
  const isBBOEnabled = getIsBBOEnabled(filterState);
  const alignPricesOnDecimal = getAlignPricesOnDecimal(filterState);
  const showLegMarketSelector = getShowLegMarketSelector(filterState);
  const showRFQ = getShowRFQ(filterState);

  useEffect(() => {
    mixpanel.track(MixpanelEvent.ViewPriceGridAlignPricesOnDecimalState, {
      [MixpanelEventProperty.Enabled]: alignPricesOnDecimal,
      [MixpanelEventProperty.Source]: source,
    });
    // Track current alignment state once when the grid is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mixpanel]);

  const onBBOEnabledChange = useCallback(
    (enabled: boolean) => {
      mixpanel.track(MixpanelEvent.TogglePriceGridBestBidOfferHighlighting, {
        [MixpanelEventProperty.Enabled]: enabled,
        [MixpanelEventProperty.Source]: source,
      });
      setFilterState(setIsBBOEnabled(filterState, enabled));
    },
    [filterState, mixpanel, setFilterState, source]
  );

  const onAlignPricesOnDecimalChange = useCallback(
    (enabled: boolean) => {
      mixpanel.track(MixpanelEvent.TogglePriceGridAlignPricesOnDecimal, {
        [MixpanelEventProperty.Enabled]: enabled,
        [MixpanelEventProperty.Source]: source,
      });
      setFilterState(setAlignPricesOnDecimal(filterState, enabled));
    },
    [filterState, mixpanel, setFilterState, source]
  );

  const onShowRFQChange = useCallback(
    (enabled: boolean) => {
      setFilterState(setShowRFQ(filterState, enabled));
    },
    [filterState, setFilterState]
  );

  const onShowLegMarketSelectorChange = useCallback(
    (enabled: boolean) => {
      setFilterState(setShowLegMarketSelector(filterState, enabled));
    },
    [filterState, setFilterState]
  );

  return useMemo(
    () => ({
      alignPricesOnDecimal,
      onAlignPricesOnDecimalChange,
      showLegMarketSelector,
      onShowLegMarketSelectorChange,
      isBBOEnabled,
      onBBOEnabledChange,
      showRFQ,
      onShowRFQChange,
    }),
    [
      alignPricesOnDecimal,
      onAlignPricesOnDecimalChange,
      showLegMarketSelector,
      onShowLegMarketSelectorChange,
      isBBOEnabled,
      onBBOEnabledChange,
      showRFQ,
      onShowRFQChange,
    ]
  );
}
